import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { StyleSpecification } from "@vietmap/vietmap-gl-js/dist/vietmap-gl";
import { Sparkles } from "lucide-react";
import { STYLE_TEMPLATES, type StyleTemplate } from "../../config/style-templates";
import { ICON_SET_PREVIEW, ICON_SETS } from "../../config/icon-sets";
import { applyPaletteTheme, COLOR_PALETTES } from "../../libs/color-palettes";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MapThumb } from "./map-thumb";
import { SetIcon, useIconSet } from "./icon-set-source";

/** Hà Nội old quarter — close enough to see roads, water and parks in the palette. */
const THUMB_CENTER: [number, number] = [105.8455, 21.0285];
const THUMB_ZOOM = 12.2;

type Props = {
  /** Vietmap preset URL templates (`{apikey}` placeholder). */
  styleUrls: Record<string, string>;
  apiKey: string;
  onOpen(template: StyleTemplate): void;
};

/** "Mẫu gợi ý" on the home page: base style + palette + icon set, one click to start. */
export function StyleTemplatesSection({ styleUrls, apiKey, onOpen }: Props) {
  const { t } = useTranslation();
  return (
    <section className="space-y-4" data-wd-key="style-templates">
      <div className="flex items-center gap-2">
        <Sparkles className="size-4 text-amber-500" />
        <h2 className="text-lg font-medium">{t("Mẫu gợi ý")}</h2>
        <Badge variant="secondary">{t("{{n}} mẫu", { n: STYLE_TEMPLATES.length })}</Badge>
      </div>
      <p className="-mt-2 text-sm text-muted-foreground">
        {t("Bắt đầu nhanh với màu nền và bộ icon đã phối sẵn, rồi chỉnh tiếp theo ý bạn.")}
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {STYLE_TEMPLATES.map((tpl) => (
          <TemplateCard key={tpl.id} template={tpl} styleUrls={styleUrls} apiKey={apiKey} onOpen={() => onOpen(tpl)} />
        ))}
      </div>
    </section>
  );
}

function TemplateCard({ template, styleUrls, apiKey, onOpen }: { template: StyleTemplate; styleUrls: Record<string, string>; apiKey: string; onOpen(): void }) {
  const { t } = useTranslation();
  const palette = COLOR_PALETTES.find((p) => p.id === template.paletteId);
  const iconSet = ICON_SETS.find((s) => s.id === template.iconSetId) ?? null;
  const base = palette?.base ?? template.base;
  const previewUrl = apiKey && styleUrls[base] ? styleUrls[base].replace("{apikey}", apiKey) : "";
  // Palette colours on, Vietmap POIs off — the icon chip shows the template's
  // set instead (its sprite only exists once the map is created).
  const transformStyle = useMemo(
    () => (style: StyleSpecification) => {
      const layers = (palette?.colors ? applyPaletteTheme(style.layers, palette) : style.layers).filter(
        (l) => l.type !== "symbol" || !l.layout?.["icon-image"]
      );
      return { ...style, layers };
    },
    [palette]
  );
  const icons = useIconSet(iconSet, apiKey);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={t("Tạo bản đồ từ mẫu {{name}}", { name: t(template.name) })}
      className="group gap-0 overflow-hidden py-0 shadow-none transition-colors outline-none hover:border-primary/40 focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:hover:border-blue-400/40"
    >
      <div className="relative border-b">
        <MapThumb
          styleUrl={previewUrl}
          themeMode={template.thumbMode}
          center={THUMB_CENTER}
          zoom={THUMB_ZOOM}
          transformStyle={transformStyle}
          className="aspect-video w-full"
        />
        {/* Icon set preview. */}
        <div className="absolute start-2 bottom-2 flex items-center gap-1 rounded-md border border-black/5 bg-white/90 px-1.5 py-1 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/85">
          {icons.status === "ready"
            ? ICON_SET_PREVIEW.map((name) => <SetIcon key={name} loaded={icons.loaded} name={name} size={18} />)
            : ICON_SET_PREVIEW.map((name) => <Skeleton key={name} className="size-[18px] rounded-full" />)}
        </div>
      </div>
      <CardContent className="space-y-1.5 px-4 py-3">
        <h3 className="text-sm font-semibold group-hover:text-primary dark:group-hover:text-blue-300">{t(template.name)}</h3>
        <p className="line-clamp-2 text-xs text-muted-foreground">{t(template.description)}</p>
        <div className="flex flex-wrap gap-1">
          <Badge variant="outline" className="font-normal text-muted-foreground">
            {template.base === "vietmap-hybrid" ? t("Vệ tinh") : t(palette?.name ?? "Mặc định")}
          </Badge>
          {iconSet && (
            <Badge variant="outline" className="font-normal text-muted-foreground">
              {t("Icon {{name}}", { name: t(iconSet.name) })}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
